import firebaseConfig from 'configs/FirebaseConfig';
import moment from 'moment';
/**
 * @constant {string[]} expectedHeaders
 * @description An array of strings representing the expected headers for a dataset.
 * These headers are used to validate the structure of a parsed dataset.
 * The expected headers include:
 * - EmployeeId: Identifier for the employee.
 * - Language: Language preference.
 * - Group: Group or team affiliation.
 * - Email: Email address of the employee.
 * - Passcode: Passcode for authentication.
 * - Unit: Organizational unit or department.
 * - Role: Job role or title.
 * - ValidThrough: Date indicating validity or expiration.
 */
export const expectedHeaders = ["EmployeeId", "Language", "Group", "Email", "Passcode", "Unit", "Role", "ValidThrough"];

export const expectedWebEnrollmentHeaders = ["EmployeeId", "Language", "Group", "Email", "Unit", "Role", "ValidThrough"];


/**
 * Messages and label constants used throughout the form.
 */
export const AddEditUserFormMessages = {
  EDIT_USER_TEXT: 'Edit and manage user details after inviting them to DNA Cloud.',
  ADD_USER_TEXT: 'Add and manage user details before inviting them to DNA Cloud',
  ENROLLMENT_TYPE_SUBTITLE: 'Manage enrollment details for user(s)',
  EMPLOYEE_ID_LABEL: 'Employee ID',
  EMPLOYEE_ID_REQUIRED: 'Oops! Employee ID is required',
  EMPLOYEE_ID_PATTERN: 'Oops! Only alphanumeric characters and . _ % + @ - are allowed',
  EMPLOYEE_ID_PLACEHOLDER: 'Enter the user’s employeeID',
  LANGUAGE_LABEL: 'Language',
  LANGUAGE_REQUIRED: 'Oops! Language is required',
  LANGUAGE_PATTERN: 'Oops! Only alphabetic characters are allowed',
  LANGUAGE_MAX: 'Oops! Maximum 50 characters allowed',
  LANGUAGE_PLACEHOLDER: 'Enter the user’s language',
  GROUP_LABEL: 'Group',
  GROUP_REQUIRED: 'Oops! Group is required',
  GROUP_PATTERN: 'Oops! Only alphanumeric characters are allowed',
  GROUP_MAX: 'Oops! Maximum 50 characters allowed',
  GROUP_PLACEHOLDER: 'Enter group name',
  EMAIL_LABEL: 'Email',
  EMAIL_VALID: 'Oops! Email address must be valid',
  EMAIL_PLACEHOLDER: 'Enter the user’s email address',
  PASSCODE_LABEL: 'Passcode',
  PASSCODE_PLACEHOLDER: 'Enter passcode',
  UNIT_LABEL: 'Unit',
  UNIT_PATTERN: 'Oops! Only alphanumeric characters are allowed',
  UNIT_MAX: 'Oops! Maximum 50 characters allowed',
  UNIT_PLACEHOLDER: 'Enter user’s unit',
  ROLE_LABEL: 'Role',
  ROLE_PLACEHOLDER: 'Select user’s role',
  EXPIRY_DATE_LABEL: 'Expiry Date',
  EXPIRY_DATE_PLACEHOLDER: 'Select expiry date',
  BACK_BUTTON: 'Back',
  CLEAR_BUTTON: 'Clear',
  SAVE_BUTTON: 'Save',
  ADD_BUTTON: 'Add',
  USER_EXISTS_ERROR: 'User already exists',
  USER_UPDATE_FAILED: 'User update failed',
  USER_ADD_FAILED: 'User add failed',
  FIELD_ERROR_PREFIX: 'Oops!',
  COMPARISON_SOURCE: 'Comparison Source',
  ENROLLMENT_TYPE:'Enrollment Type',
  SECURITY_TYPE:'Security Type',
  EXPIRATION_PERIOD:'Expiration Period',
  ADD_NUMBER_OF_REMINDER:'Add Number of Reminder',
  IG_PROFILE_SELECTION: 'Select Existing IG Profile',

};

/**
 * Messages constant to store all text strings used in the AddUserDrawer component.
 */
export const AddUserDrawerMessages = {
  EDIT_SINGLE_USER_TITLE: 'Edit Single User',
  ADD_SINGLE_USER_TITLE: 'Add Single User',
  ADD_MULTIPLE_USERS_TITLE: 'Add Multiple Users',
  DEFAULT_TITLE: 'Add Users',
  ENROLLMENT_TYPE_TITLE: 'Generate Enrollment Type',
  HELP_TIP_TEXT: 'Tip: It is important to format your data before importing to avoid errors. Download our CSV template to show you how'
};

// Constants for file size and maximum number of files
export const MAX_SIZE_PER_FILE_IN_MB = 3;
export const MAX_USER_PER_FILE = 5000;
export const GROUP_MAX_LENGTH = 50;
export const LANGUAGE_MAX_LENGTH = 50;
export const MAX_FILES = 1;
export const MAX_USERS = 5000;
// Object containing various UI text messages and labels
export const BulkUploadMessages = {
  TIP_HEADER: 'Select the files you want to import.',
  TIP_NOTE: 'Tip: It is important to format your data before uploading to avoid errors.',
  TIP_TEMPLATE: 'Download our CSV template to show you how.',
  DOWNLOAD_LABEL: 'Download CSV',
  DROP_PROMPT: 'Drop files here to upload',
  SUPPORTS: 'Supports: CSV',
  MAX_SIZE: (sizeMb) => `Max file size ${sizeMb}MB`,
  MAX_USER: (userLimit) => `Max user ${userLimit}`,
  FILE_SECTION_TITLE: 'Files For Import:',
  VALIDATING: 'Validating...',
  WAITING: 'Waiting to upload...',
  SUCCESS: 'Uploaded successfully',
  INVALID_TYPE: 'Oops! Only .csv files are allowed.',
  DUPLICATE: 'Oops! This file has already been added.',
  FILE_TOO_LARGE: (limitMb) => `Oops! File exceeds ${limitMb}MB file limit.`,
  CLEAR: 'Clear',
  UPLOAD: 'Upload',
  BACK: 'Back',
  DEFAULT_ERROR: 'Upload failed',
};


/**
 * Messages constant to store all text strings used in the ReplaceUserModal component.
 */
export const ReplaceUserModalMessages = {
  MODAL_TITLE: 'Do you want to replace the User?',
  CANCEL_BUTTON_TEXT: 'Cancel',
  REPLACE_BUTTON_TEXT: 'Replace User',
  MODAL_CONTENT: (employeeId) =>
    `The user ${employeeId} already exists in your environment. Would you like to replace the existing user with the newly created one?`,
};

// Messages constant to store all text strings used in the component
export const UserAddOptionsMessages = {
  TITLE_MESSAGE: 'Select if you are adding one or multiple users to DNA Cloud',
  SINGLE_USER_BUTTON_TEXT: 'Add a single user',
  SINGLE_USER_DESCRIPTION: 'Enter user detail one at a time',
  MULTIPLE_USERS_BUTTON_TEXT: 'Add multiple users',
  MULTIPLE_USERS_DESCRIPTION: 'Import users in bulk from a file',
};

// Messages constant to store all text strings used in the component
export const ImportJobHistoryMessages = {
  MODAL_TITLE: 'View Import History',
  INSTRUCTION_TEXT: 'If importing a large dataset, it may take some time to complete. Please check back later.',
  CSV_FILE_NAME: 'CSV File Name',
  DATE: 'Date',
  STATUS: 'Status',
  ACTIONS: 'Actions',
  DOWNLOAD_ERROR_LOG: 'Download Error Log',
  DONE: 'Done',
};

export const ADD_USER_ROUTE = 'IGUserManagement/v1/users';
export const EDIT_USER_ROUTE = 'IGUserManagement/v1/users';
export const LIST_JOB_ROUTE = 'IGUserManagement/v1/users/bulk';
export const FETCH_DECRYPTED_PASSCODE_ROUTE = 'IGUserManagement/v1/passcode/decrypt';
export const BULK_UPLOAD_USER_ROUTE = `https://${process.env.REACT_APP_REGION}-${firebaseConfig.projectId}.cloudfunctions.net/IGUserManagement/v1/users/bulk`;
export const OPTIONAL_USER_FIELDS = ['email', 'unit', 'role', 'validThrough']
export const PASSCODE = 'passcode'
export const VALID_THROUGH = 'validThrough'
export const LOGIN_HISTORY_ROUTE = 'IGUserLoginHistory/v1/users';
export const EXPORT_LOGIN_HISTORY_ROUTE = 'IGUserLoginHistoryExport/v1/users';

/* 
* PresetOptions constant to store predefined date ranges for filtering or reporting purposes.
* Each option includes a label for display and a corresponding date range calculated using the moment library.
*/
export const PresetOptions = [
  {
    label: 'Last 7 Days',
    range: [moment().subtract(7, 'days'), moment()],
  },
  {
    label: 'Last 14 Days',
    range: [moment().subtract(14, 'days'), moment()],
  },
  {
    label: 'Last 30 Days',
    range: [moment().subtract(30, 'days'), moment()],
  },
  {
    label: 'This week',
    range: [moment().startOf('week'), moment().endOf('week')],
  },
  {
    label: 'Last week',
    range: [moment().subtract(1, 'week').startOf('week'), moment().subtract(1, 'week').endOf('week')],
  },
  {
    label: 'This Month',
    range: [moment().startOf('month'), moment().endOf('month')],
  },
  {
    label: 'Last Month',
    range: [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
  },
  // // Custom option does not have a predefined range and is meant to allow users to select their own date range.
  // {
  //   label: 'Custom',
  //   range: null,
  // },
]


export const COMPARISON_TYPES = {
    BARCODE: 'barcode',
    CLOUD: 'cloud',
    NFC: 'nfc',
} 

// EDCL-293 -->

export const FILE_UPLOAD_STATUS = {
  VALIDATING: 'validating',
  PENDING: 'pending',
  UPLOADING: 'uploading',
  DONE: 'done',
  ERROR: 'error',
};

export const POP_CONFIRM_MESSAGE = {
    ZEBRA: `By enrolling in Zebra Biometric Authentication, you consent and agree to the collection, processing and storage of your biometric data by Zebra Technologies and your employer. Zebra does not store or retain biometric data separately from the encrypted QR code generated during enrollment and biometric signatures are deleted after printing your QR code. Additional policies, processes and procedures may apply with your employer. By continuing to enroll you acknowledge you have read and agreed to the terms stated above.`,
    RED_CORP: `Red Corp's biometric authentication service collects and processes your biometric data for authentication purposes. By enrolling, you consent to Red Corp's collection and use of your biometric data in accordance with their privacy policy. Red Corp does not store your biometric data separately and uses it solely for authentication. Please review Red Corp's privacy policy for more details on how your biometric data is handled. By continuing to enroll, you acknowledge that you have read and agreed to Red Corp's terms and privacy policy regarding biometric data.`
} 

export const ERROR_MESSAGES = {
  INVALID_LINK: 'Your link is invalid, please contact your Admin',
  EXPIRED_TOKEN: 'Token has expired. Please request a new token.', 
  INVALID_URL: 'Invalid URL',
  DEFAULT: 'Please try again later.',
  NETWORK_ERROR: 'Please check your internet connection and try again later.',
  FILE_PARSE_ERROR: 'File parsing failed',
}

export const ERROR_TYPES = {
  INVALID_LINK: 'Invalid Link',
  EXPIRED_TOKEN: 'Expired Token',
  INVALID_URL: 'Invalid URL',
  DEFAULT: 'Unknown Error',
  NETWORK_ERROR: 'Check Internet Connection'
} 

export const PASSCODE_VALIDATORS = {
  minLength: (v, rule) =>
    v.length < rule ? `Passcode must be at least ${rule} characters long` : null,
  maxLength: (v, rule) =>
    v.length > rule ? `Passcode must be at most ${rule} characters long` : null,
  minNumbers: (v, rule) => {
    const count = (v.match(/\d/g) || []).length;
    return count < rule ? `Passcode must contain at least ${rule} numeric character(s)` : null;
  },
  minUpperCase: (v, rule) => {
    const count = (v.match(/[A-Z]/g) || []).length;
    return count < rule ? `Passcode must contain at least ${rule} uppercase character(s)` : null;
  },
  minLowerCase: (v, rule) => {
    const count = (v.match(/[a-z]/g) || []).length;
    return count < rule ? `Passcode must contain at least ${rule} lowercase character(s)` : null;
  },
  minSymbols: (v, rule) => {
    const count = (v.match(/[!@#$%^&*(),.?":{}|<>]/g) || []).length;
    return count < rule ? `Passcode must contain at least ${rule} special character(s)` : null;
   },
 }

 export const ACTIVE_TYPE_TAG_CONFIG = {
   Expired: 'red',
   Active: 'green',
   default: 'orange',
 };
// <-- EDCL-293